import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { MemberInfo } from '../types';

interface ProfileHeaderProps {
  member: MemberInfo;
  isOwnProfile: boolean;
  onEdit: () => void;
  onMessage: () => void;
}

const ProfileHeader = ({ member, isOwnProfile, onEdit, onMessage }: ProfileHeaderProps) => {
  const [imageError, setImageError] = useState(false);

  const formatJoinDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-shrink-0">
          <div className="relative w-32 h-32 rounded-full overflow-hidden bg-muted">
            {!imageError ? (
              <Image
                src={member.avatar}
                alt={`Profile photo of ${member.name}`}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary">
                <span className="text-4xl font-semibold text-primary-foreground">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                {member.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Mail" size={16} />
                  <span>{member.email}</span>
                </div>
                {member.phone && (
                  <div className="flex items-center gap-1">
                    <Icon name="Phone" size={16} />
                    <span>{member.phone}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {isOwnProfile ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="flex items-center gap-2"
                >
                  <Icon name="Pencil" size={16} />
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onMessage();
                    }}
                    className="flex items-center gap-2"
                  >
                    <Icon name="MessageSquare" size={16} />
                    Message
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit();
                    }}
                    className="flex items-center gap-2"
                  >
                    <Icon name="Pencil" size={16} />
                    Edit
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-sm">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Role</div>
                <div className="text-sm font-medium text-foreground capitalize">
                  {member.role}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-muted rounded-sm">
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} color="var(--color-success)" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Member Since</div>
                <div className="text-sm font-medium text-foreground">
                  {formatJoinDate(member.joinDate)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-muted rounded-sm">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="text-sm font-medium text-success">Active</div>
              </div>
            </div>
          </div>

          {member.address && (
            <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
              <Icon name="MapPin" size={16} className="mt-0.5" />
              <span>{member.address}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;